/**
 * i18n Provider — Enviar Web
 *
 * Wraps @lingui/core directly (no community fork dependency) with a
 * SolidJS-native reactive layer. Locale changes update a Signal, which
 * causes all useLingui() consumers to re-derive translations reactively
 * without any component re-render overhead.
 *
 * Lazy-loading: Only the active locale's JS catalog is fetched at startup
 * (and when the user switches language), keeping the initial bundle minimal.
 *
 * Persistence: localStorage only — the app is fully offline and accountless.
 *
 * RTL support: Sets document.documentElement.dir automatically for ar / he.
 */

import { i18n, type MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import {
  createContext,
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";
import { RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "@enviar/config/locales";

// Initialize with a default locale so that early translation calls do not throw errors
i18n.load("en", {});
i18n.activate("en");

// ─── Types ────────────────────────────────────────────────────────────────────

interface I18nContextValue {
  /** Reactive current locale code */
  locale: () => SupportedLocale;
  /** Change locale — reloads catalog and persists to localStorage */
  setLocale: (locale: SupportedLocale) => Promise<void>;
  /** True while the catalog is loading */
  isLoading: () => boolean;
  /**
   * Reactive translation function.
   *
   * TYPE BARRIER (on purpose narrower than the implementation): it only accepts
   * `MessageDescriptor`, that is the result of a macro (`` msg`…` ``). A plain string
   * —`t("My Day")`— **does not compile**, and that is the important part: the Lingui extractor only sees
   * the macros, so a plain string never enters the catalog and stays in English in
   * non-English locales WITHOUT any gate saying so. The implementation (`translate`)
   * still accepts a string and resolves it by hash, so nothing blows up at runtime; the
   * type makes it impossible to write.
   */
  t: (message: MessageDescriptor, values?: Record<string, unknown>) => string;
  /**
   * Translates a DYNAMIC key: a string that is not known at the call site because
   * it comes from a data table or from a function.
   *
   * It is only correct if that exact text is declared with `` msg`…` `` in a MANIFEST
   * that the extractor does see, because then the id-hash matches and the catalog resolves it.
   * Without a manifest, the string is NOT extracted and the user sees it in English in the non-
   * English locales.
   *
   * It exists so the escape from the strict type of `t` is EXPLICIT and greppable, instead of
   * a silent `as`. Each use must point to its manifest.
   */
  tDynamic: (message: string, values?: Record<string, unknown>) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue>();

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

// ─── Catalog Loader ───────────────────────────────────────────────────────────

interface CatalogModule {
  messages: Record<string, string>;
}

function assertCatalogModule(mod: unknown): asserts mod is CatalogModule {
  if (typeof mod !== "object" || mod === null || !("messages" in mod)) {
    throw new Error("Invalid catalog module");
  }
}

/** A catalog already imported, ready to be handed to i18n. */
interface FetchedCatalog {
  /** The locale that REALLY could be loaded (== the requested one, except fallback to "en"). */
  locale: SupportedLocale;
  messages: Record<string, string>;
}

/**
 * Dynamically imports the compiled .js catalog for the requested locale.
 * Vite will code-split each locale into a separate chunk automatically.
 *
 * ⚠️ This function is PURE with respect to i18n: it imports and returns, but does NOT call
 * `i18n.load`/`i18n.activate`. It used to, and that is where the race was: the
 * import is asynchronous and there were TWO lanes launching it (startup in
 * `onMount` and the user change in `setLocale`), without any guard. The slow
 * startup import landed AFTER the locale change and re-activated
 * its own. Now who decides whether a result is still current is `applyLocale`, and the
 * side effect on i18n happens AFTER that check.
 */
async function fetchCatalog(locale: SupportedLocale): Promise<FetchedCatalog> {
  try {
    const rawModule: unknown = await import(`../../locales/${locale}/messages.mjs`);
    assertCatalogModule(rawModule);
    return { locale, messages: rawModule.messages };
  } catch (err) {
    console.warn(`[i18n] Failed to load catalog for "${locale}", falling back to "en"`, err);
    // Fall back to English if catalog missing
    const rawEnModule: unknown = await import("../../locales/en/messages.mjs");
    assertCatalogModule(rawEnModule);
    return { locale: "en", messages: rawEnModule.messages };
  }
}

/** Applies correct RTL/LTR direction on <html> element */
function applyDirection(locale: SupportedLocale) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  document.documentElement.lang = locale;
}

/** Clean type-guard to verify if an unknown string represents a SupportedLocale */
function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return typeof locale === "string" && SUPPORTED_LOCALES.some((code) => code === locale);
}

/** Detect browser locale and match against supported locales */
function detectBrowserLocale(): SupportedLocale {
  const browser = navigator.language.split("-")[0]?.toLowerCase() ?? "en";
  return isSupportedLocale(browser) ? browser : "en";
}

const LOCALE_STORAGE_KEY = "@app_locale";

// ─── Provider ─────────────────────────────────────────────────────────────────

export const I18nProvider: ParentComponent = (props) => {
  const [locale, setLocaleSignal] = createSignal<SupportedLocale>("en");
  const [isLoading, setIsLoading] = createSignal(true);
  // Bumped after every REAL catalog load. Consumers (`translate`) depend on it so
  // they re-derive when a catalog finishes loading even if the `locale` signal
  // value didn't change. The module init seeds an EMPTY "en" catalog
  // (`i18n.load("en", {})`) so early calls don't throw; the first real load on
  // mount for a user whose locale IS the default "en" would set the signal to its
  // current value ("en") — a NO-OP that skips reactivity, so consumers keep the
  // empty catalog. In dev the lingui macro inlines each message's default text so
  // strings still render, but in a PRODUCTION build the macro strips those
  // defaults, so those strings render as their raw hash id. Bumping this signal
  // after each load forces the re-derive regardless of whether `locale` changed.
  const [catalogVersion, setCatalogVersion] = createSignal(0);

  // There are TWO lanes requesting locale and both are asynchronous: startup
  // (`onMount`) and the user change (`setLocale`). They truly overlap: if the
  // startup catalog import is still in flight when the user chooses another
  // locale, it lands AFTER and, without a guard, it was applied anyway. `applyToken` makes
  // only the MOST RECENT request touch anything ("last write wins"); a stale one is
  // discarded BEFORE handing its catalog to i18n.
  let applyToken = 0;
  // Unmounting invalidates any in-flight `applyLocale`: its catalog import can land after the
  // provider is gone, and `i18n.activate` would then run against a torn-down tree.
  onCleanup(() => {
    applyToken++;
  });

  /**
   * Applies a locale:
   * 1. Imports the catalog (without touching i18n yet)
   * 2. If the request is still current: hands it to i18n and activates
   * 3. Updates the reactive signal + `catalogVersion` (forces re-derive)
   * 4. Applies the `dir`/`lang` of <html>
   *
   * Returns `true` if this request was the one applied, `false` if another
   * newer one made it stale (the caller must NOT persist anything in that case).
   */
  const applyLocale = async (next: SupportedLocale): Promise<boolean> => {
    const token = ++applyToken;
    try {
      setIsLoading(true);
      const fetched = await fetchCatalog(next); // only the import: does not touch i18n
      if (token !== applyToken) {
        // Overtaken by a newer request (which is now the owner of isLoading).
        // The catalog is discarded WITHOUT handing it to i18n: activating it here reverted the
        // locale that the current request had already set (the slow startup import
        // landed after the user change and returned the UI to English).
        return false;
      }
      // The locale that REALLY entered is loaded/activated: if there was a fallback, i18n stays
      // consistent with what is really in memory.
      i18n.load(fetched.locale, fetched.messages);
      i18n.activate(fetched.locale);
      // Force consumers to re-derive now that the real catalog is live, even if
      // `next` equals the signal's "en" default (an otherwise no-op set that would
      // leave the whole UI showing raw hash ids in a prod build).
      setCatalogVersion((v) => v + 1);
      setLocaleSignal(next);
      applyDirection(next);
      return true;
    } finally {
      // Only the newest request owns `isLoading`: a superseded one must not clear the flag of the
      // load still in flight, and a rejected load (both catalog imports failed) must not leave it
      // stuck on.
      if (token === applyToken) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Changes the active locale (user-initiated) and persists it to localStorage.
   */
  const setLocale = async (newLocale: SupportedLocale): Promise<void> => {
    if (newLocale === locale()) {
      return;
    }
    const applied = await applyLocale(newLocale);
    if (!applied) {
      // A later choice rules: persisting this one would leave localStorage
      // pointing to a locale different from the one being shown.
      return;
    }

    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch (err) {
      console.warn("[i18n] Could not persist locale to localStorage:", err);
    }
  };

  // On mount: load the initial locale (from localStorage or browser)
  onMount(() => {
    const init = async () => {
      try {
        const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
        const userLocale = isSupportedLocale(storedLocale) ? storedLocale : detectBrowserLocale();

        // `applyLocale` already manages `isLoading` (and does NOT turn it off if it becomes stale:
        // that flag becomes the new request's).
        await applyLocale(userLocale);
      } catch (err) {
        console.warn("[i18n] Failed to initialize i18n:", err);
        setIsLoading(false);
      }
    };
    void init();
  });

  const translate = (
    message: string | MessageDescriptor,
    values?: Record<string, unknown>,
  ): string => {
    locale(); // Establish dynamic reactive dependency on SolidJS signal
    catalogVersion(); // Re-derive when a catalog finishes loading (see applyLocale)
    if (typeof message === "string") {
      // The compiled catalog uses HASH ids (generateMessageId), same as the macro
      // `msg`...``. A plain string (`t("My Day")`) would look for id="My Day" and not
      // resolve (it would show English in any locale). We compute the same id
      // as the macro so `t("literal")` translates the same as `t(msg`literal`)`,
      // with the text itself as fallback. (Fix for the "locale does not reflect" bug.)
      return i18n._({ id: String(generateMessageId(message)), message, values });
    }
    // Values also apply to descriptors (they go INSIDE the descriptor in
    // Lingui's API): without them, messages like msg`...{{date}}...`
    // rendered the empty placeholder ("{}"). The descriptor is built in
    // a separate variable: the Lingui extractor does not know how to parse a spread
    // inline inside `i18n._()` and aborts the file's extraction.
    const descriptor: MessageDescriptor = {
      ...message,
      values: { ...message.values, ...values },
    };
    return i18n._(descriptor);
  };

  return (
    <I18nContext.Provider
      value={{ locale, setLocale, isLoading, t: translate, tDynamic: translate }}
    >
      {props.children}
    </I18nContext.Provider>
  );
};
